// SPDX-License-Identifier: AGPL-3.0-or-later

import {bootstrapSyntheticHistory} from '@app/app/HistoryBootstrap';
import {installScrollRestoration} from '@app/features/platform/components/router/ScrollRestoration';
import {configure} from 'mobx';

if (typeof window !== 'undefined' && window.history) {
	bootstrapSyntheticHistory();
	installScrollRestoration();
}
configure({
	disableErrorBoundaries: true,
	enforceActions: 'observed',
});
import '@app/app/globals.css';
import '@app/features/theme/styles/generated/color-system.css';
import '@app/features/theme/styles/preflight.css';
import 'urlpattern-polyfill';
import {initI18n} from '@app/app/I18n';
import {Routes} from '@app/app/Routes';
import {BootstrapErrorScreen} from '@app/features/app/components/BootstrapErrorScreen';
import {ErrorFallback} from '@app/features/app/components/ErrorFallback';
import {NetworkErrorScreen} from '@app/features/app/components/NetworkErrorScreen';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {i18n} from '@lingui/core';
import {I18nProvider} from '@lingui/react';
import React from 'react';
import ReactDOM from 'react-dom/client';

type AuthenticationCommandsModule = typeof import('@app/features/auth/commands/AuthenticationCommands');

type NativeUtilsModule = typeof import('@app/features/ui/utils/NativeUtils');
interface AppErrorBoundaryProps {
	fallback?: (error: Error | null) => React.ReactNode;
	children?: React.ReactNode;
}

const logger = new Logger('index');
class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, {hasError: boolean; error: Error | null}> {
	constructor(props: AppErrorBoundaryProps) {
		super(props);
		this.state = {hasError: false, error: null};
	}
	static getDerivedStateFromError(error: Error) {
		return {hasError: true, error};
	}
	override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		logger.error('Unhandled application error:', error, errorInfo);
	}
	override render() {
		if (this.state.hasError) {
			return this.props.fallback?.(this.state.error) ?? null;
		}
		return this.props.children;
	}
}
function isQuickCssEditorPath(): boolean {
	return window.location.pathname === Routes.QUICK_CSS_EDITOR;
}
async function renderQuickCssEditor(): Promise<void> {
	const {QuickCssStandaloneApp} = await import('@app/features/app/components/pages/QuickCssEditorPage');
	const root = ReactDOM.createRoot(document.getElementById('root')!);
	root.render(
		<AppErrorBoundary
			fallback={(error) => (
				<I18nProvider i18n={i18n}>
					<ErrorFallback error={error ?? undefined} />
				</I18nProvider>
			)}
		>
			<I18nProvider i18n={i18n}>
				<QuickCssStandaloneApp />
			</I18nProvider>
		</AppErrorBoundary>,
	);
}
async function resumePendingDesktopHandoffLogin(
	getElectronAPI: NativeUtilsModule['getElectronAPI'],
	authenticationCommands: AuthenticationCommandsModule,
): Promise<void> {
	const electronApi = getElectronAPI();
	if (!electronApi || typeof electronApi.consumeDesktopHandoffCode !== 'function') {
		return;
	}
	let handoffCode: string | null = null;
	try {
		handoffCode = await electronApi.consumeDesktopHandoffCode();
	} catch (error) {
		logger.warn('Failed to consume pending desktop handoff code:', error);
		return;
	}
	if (!handoffCode) {
		return;
	}
	try {
		const result = await authenticationCommands.pollDesktopHandoffStatus(handoffCode);
		if (result.status === 'completed' && result.token && result.user_id) {
			await authenticationCommands.completeLogin({token: result.token, userId: result.user_id});
		} else {
			logger.warn('Pending desktop handoff not completed:', {status: result.status});
		}
	} catch (error) {
		logger.warn('Failed to resume pending desktop handoff login:', error);
	}
}
async function bootstrap(): Promise<void> {
	await initI18n();
	if (isQuickCssEditorPath()) {
		await renderQuickCssEditor();
		return;
	}
	const [
		{App},
		authenticationCommands,
		{setupConduit},
		{default: CaptchaInterceptor},
		{installCrashReportUploader},
		{initializeEmojiParser},
		{installRendererInstrumentation},
		{registerServiceWorker},
		{default: AccountManager},
		{default: ChannelDisplayName},
		{default: GeoIP},
		{default: Keybind},
		{default: NewDeviceMonitoring},
		{default: Notification},
		{default: QuickSwitcher},
		{default: RuntimeConfig},
		{default: StatusPage},
		{default: MediaEngineFacade},
		{preloadClientInfo},
		{getElectronAPI},
		{default: TtsUtils},
	] = await Promise.all([
		import('@app/app/App'),
		import('@app/features/auth/commands/AuthenticationCommands'),
		import('@app/app/SetupConduit'),
		import('@app/features/auth/components/CaptchaInterceptor'),
		import('@app/features/devtools/utils/CrashReportUploader'),
		import('@app/features/messaging/utils/markdown/EmojiProviderSetup'),
		import('@app/features/voice/diagnostics/VoiceDiagnosticsInstrumentation'),
		import('@app/features/platform/service_worker/Register'),
		import('@app/features/auth/state/AccountManager'),
		import('@app/features/channel/state/ChannelDisplayName'),
		import('@app/features/app/state/GeoIP'),
		import('@app/features/input/state/Keybind'),
		import('@app/features/auth/state/NewDeviceMonitoring'),
		import('@app/features/ui/state/Notification'),
		import('@app/features/search/state/QuickSwitcher'),
		import('@app/features/app/state/RuntimeConfig'),
		import('@app/features/user/state/StatusPage'),
		import('@app/features/voice/engine/MediaEngineFacade'),
		import('@app/features/platform/utils/clientInfo'),
		import('@app/features/ui/utils/NativeUtils'),
		import('@app/features/voice/utils/TtsUtils'),
	]);
	await Promise.all([
		import('@app/features/search/components/quick_switcher/QuickSwitcherModal'),
		import('@app/features/messaging/state/Spellcheck'),
	]);
	installRendererInstrumentation();
	installCrashReportUploader();
	void preloadClientInfo();
	QuickSwitcher.setI18n(i18n);
	ChannelDisplayName.setI18n(i18n);
	Keybind.setI18n(i18n);
	NewDeviceMonitoring.setI18n(i18n);
	Notification.setI18n(i18n);
	MediaEngineFacade.setI18n(i18n);
	CaptchaInterceptor.setI18n(i18n);
	TtsUtils.setI18n(i18n);
	TtsUtils.init();
	try {
		await RuntimeConfig.waitForInit();
	} catch (error) {
		logger.error('Failed to initialize runtime config:', error);
		const root = ReactDOM.createRoot(document.getElementById('root')!);
		root.render(
			<I18nProvider i18n={i18n}>
				<NetworkErrorScreen />
			</I18nProvider>,
		);
		return;
	}
	void StatusPage.checkIncidents();
	StatusPage.startPolling();
	if (!RuntimeConfig.isSelfHosted()) {
		try {
			await GeoIP.fetchGeoData();
		} catch (error) {
			logger.warn('Failed to fetch GeoIP data (continuing anyway):', error);
		}
	}
	await AccountManager.bootstrap();
	setupConduit();
	initializeEmojiParser();
	await resumePendingDesktopHandoffLogin(getElectronAPI, authenticationCommands);
	const root = ReactDOM.createRoot(document.getElementById('root')!);
	root.render(
		<AppErrorBoundary
			fallback={(error) => (
				<I18nProvider i18n={i18n}>
					<ErrorFallback error={error ?? undefined} />
				</I18nProvider>
			)}
		>
			<App />
		</AppErrorBoundary>,
	);
	registerServiceWorker();
}
bootstrap().catch(async (error) => {
	logger.error('Failed to bootstrap app:', error);
	try {
		await initI18n();
		const root = ReactDOM.createRoot(document.getElementById('root')!);
		root.render(
			<I18nProvider i18n={i18n}>
				<BootstrapErrorScreen error={error} />
			</I18nProvider>,
		);
	} catch (renderError) {
		logger.error('Failed to render error screen:', renderError);
		document.body.style.margin = '0';
		document.body.style.minHeight = '100vh';
		document.body.innerHTML = `
			<div
				style="
					min-height: 100vh;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 2rem;
					text-align: center;
					box-sizing: border-box;
				"
			>
				<p
					style="
						max-width: 32rem;
						font-size: 1.25rem;
						line-height: 1.5;
						margin: 0;
					"
				>
					Something went wrong and the app couldn't load. Please try refreshing the page.
				</p>
			</div>
		`;
	}
});
